import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Trophy, Flame, Award } from "lucide-react";
import { apiClient } from "../lib/api";
import { motion } from "framer-motion";
import UserAvatar from "../components/UserAvatar";
import { useAuth } from "../lib/auth";

export default function Profile() {
  const { username } = useParams();
  const { user: me } = useAuth();
  const [u, setU] = useState(null);
  const [achievements, setAchievements] = useState([]);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await apiClient.get(`/profile/${username}`);
        if (mounted) setU(r.data);
        const a = await apiClient.get(`/profile/${username}/achievements`);
        if (mounted) setAchievements(a.data?.achievements || []);
      } catch {/* 404 handled in render */}
    })();
    return () => { mounted = false; };
  }, [username]);

  // Prefer API stats (elo/wins/losses); keep auth avatar if it was just updated locally.
  const display = (me?.username === username && u)
    ? { ...u, avatar_url: me.avatar_url ?? u.avatar_url }
    : u;
  if (!display) return <div className="min-h-[70vh] grid place-items-center text-soft">Loading profile…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="sm-card p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <UserAvatar src={display.avatar_url} size={96} glow />
        <div className="flex-1">
          <div className="font-mono text-[10px] tracking-[0.4em] text-blue uppercase">PROFILE</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl">{display.username}</h1>
          <div className="text-soft mt-2 text-sm">{display.email}</div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 text-center w-full sm:w-auto">
          <Stat label="ELO"    value={display.elo}      accent="text-blue" />
          <Stat label="Rank"   value={display.rank}     accent="text-purple" />
          <Stat label="Wins"   value={display.wins}     accent="text-win" />
          <Stat label="Losses" value={display.losses}   accent="text-loss" />
        </div>
      </motion.div>

      <div className="sm-card p-6 mt-6">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.4em] text-blue uppercase"><Trophy size={12} /> Achievements</div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {achievements.length === 0 && <div className="text-soft text-sm">No achievements yet — win a few matches to unlock badges.</div>}
          {achievements.map((a, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
              <Award className="w-6 h-6 mx-auto text-yellow-400" />
              <div className="font-heading font-bold text-sm mt-2">{a.name}</div>
              <div className="text-faint text-[11px] mt-0.5">{a.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
      <div className="text-[10px] tracking-[0.25em] text-faint uppercase">{label}</div>
      <div className={`font-heading font-black text-2xl ${accent}`}>{value ?? "—"}</div>
    </div>
  );
}
